import { Secret } from '@kindo/universal';

import {
  Button,
  ButtonType,
  Callout,
  CalloutType,
  Icon,
  Icons,
  TextIconColor,
  Typography,
  TypographyAlignment,
  TypographySize
} from '~/components/core';
import { Content } from '~/types';

interface ShareWorkflowConfirmationProps {
  onClose: () => void;
  onConfirm: () => void;
  secrets: Pick<Secret, 'id' | 'label'>[];
  staticContent: Pick<Content, 'fileName'>[];
}
const getShareWorkflowConfirmationTitle = ({
  staticContent,
  secrets
}: {
  secrets: Pick<Secret, 'id' | 'label'>[];
  staticContent: Pick<Content, 'fileName'>[];
}): string => {
  if (staticContent.length > 0 && secrets.length > 0) {
    return 'Share agent with included files and secrets';
  }
  if (staticContent.length > 0) {
    return 'Share agent with included files';
  }
  if (secrets.length > 0) {
    return 'Share agent with included secrets';
  }
  return 'Share agent';
};

const renderResourceItem = (icon: Icon, label: string, dataTestId: string) => (
  <div
    className="flex items-center gap-2 rounded-md bg-white/5 p-2"
    data-testid={dataTestId}
  >
    <Icons color={TextIconColor.SECONDARY} icon={icon} />
    <Typography
      color={TextIconColor.PRIMARY}
      maxWidth="max-w-md"
      size={TypographySize.SMALL}
    >
      {label}
    </Typography>
  </div>
);

const renderResourceSection = (
  title: string,
  calloutType: CalloutType,
  calloutMessage: string,
  dataTestId: string,
  children: React.ReactNode
) => (
  <div className="flex flex-col gap-4">
    <Typography color={TextIconColor.SECONDARY}>{title}</Typography>
    <div className="rounded-lg bg-white/5 p-4" data-testid={dataTestId}>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
    <Callout type={calloutType}>
      <Typography color={TextIconColor.PRIMARY} size={TypographySize.SMALL}>
        {calloutMessage}
      </Typography>
    </Callout>
  </div>
);

const ShareWorkflowConfirmation: React.FC<ShareWorkflowConfirmationProps> = ({
  staticContent,
  secrets,
  onClose,
  onConfirm
}) => (
  <div
    className="flex flex-col gap-6"
    data-testid="share-workflow-confirmation"
  >
    {/* Header */}
    <Typography
      alignment={TypographyAlignment.CENTER}
      color={TextIconColor.PRIMARY}
      size={TypographySize.H3}
    >
      {getShareWorkflowConfirmationTitle({ staticContent, secrets })}
    </Typography>

    {/* Static Content Section */}
    {staticContent.length > 0 &&
      renderResourceSection(
        'The agent you are about to share includes the following files:',
        CalloutType.WARNING,
        'The contents of these files could be exposed to users who run this agent.',
        'share-workflow-static-content-items',
        staticContent.map((content) => (
          <div key={content.fileName}>
            {renderResourceItem(
              Icon.FILE,
              content.fileName || '',
              'share-workflow-static-content-item'
            )}
          </div>
        ))
      )}

    {/* Secrets Section */}
    {secrets.length > 0 &&
      renderResourceSection(
        'The agent you are about to share will use the following secrets when executed:',
        CalloutType.SECURITY,
        'Your secrets will be used when shared users run this agent, but they cannot view the values or edit the API steps.',
        'share-workflow-secrets-items',
        secrets.map((secret) => (
          <div key={secret.id}>
            {renderResourceItem(
              Icon.LOCK,
              secret.label,
              'share-workflow-secret-item'
            )}
          </div>
        ))
      )}

    {/* Confirmation */}
    <div className="flex flex-col gap-4 border-t border-white/10 pt-4">
      <Typography
        alignment={TypographyAlignment.CENTER}
        color={TextIconColor.SECONDARY}
      >
        Would you like to continue sharing this agent?
      </Typography>
      <div
        className="flex items-center justify-end gap-3"
        data-testid="share-workflow-confirmation-buttons"
      >
        <Button label="Cancel" onClick={onClose} type={ButtonType.OUTLINED} />
        <Button label="Share" onClick={onConfirm} type={ButtonType.SOLID} />
      </div>
    </div>
  </div>
);

export default ShareWorkflowConfirmation;
